import { Button, Divider, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  accountsApi,
  campaignsApi,
  useGetCampaignsQuery,
  useGetCompaniesFiltersQuery,
} from "../api";
import { SelectedFilterList } from "../components/features/SelectedFilterList";
import { ChevronLeftIcon, PlusIcon } from "../components/images";
import {
  NoCampaignsBlock,
  NoResultFound,
  ProductListItem,
} from "../components/products";
import { CustomLoader } from "../components/ui/CustomLoader";
import { DropdownWithSearch } from "../components/ui/dropdown";
import { InputWithQuery } from "../components/ui/InputWithQuery";
import {
  useDebounceValue,
  useStateWithSearchParams,
  useAppDispatch,
} from "../hooks";
import { AppRoutes } from "../types/enums";
import { Campaign } from "../types/apiTypes";
import { QUERY_SEPARATOR } from "../constant";
import { clearUpdatedCompanies } from "../features/companySlice";

type SearchParams = {
  search?: string;
  company?: string;
};

const ProductsPage = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [isDemoCalaps, setIsDemoCalaps] = useState<boolean>(false);
  const [demoCampaign, setDemoCampaign] = useState<Campaign | null>(null);
  const { searchDict, setSearchValue } = useStateWithSearchParams<SearchParams>(
    {
      search: undefined,
      company: undefined,
    },
  );

  useEffect(() => {
    dispatch(accountsApi.util.resetApiState());
    dispatch(campaignsApi.util.resetApiState());
    dispatch(clearUpdatedCompanies());
  }, []);

  const searchValueDebounce = useDebounceValue(searchDict.search || "");
  const companiesValue = searchDict.company?.split(QUERY_SEPARATOR);

  const { data: companiesFilters } = useGetCompaniesFiltersQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const { data, isFetching } = useGetCampaignsQuery(
    {
      search: searchValueDebounce || undefined,
      companies: companiesValue,
    },
    {
      refetchOnMountOrArgChange: true,
    },
  );

  useEffect(() => {
    if (pathname === AppRoutes.HOME)
      navigate(AppRoutes.CAMPAINGS, { replace: true });
  }, [pathname]);

  const companies = (companiesFilters?.items || []).map(({ name }) => name);

  const removeFilterHandler = (key: string, filter: string) => {
    const filters = searchDict[key as keyof SearchParams];
    if (!filters) return;

    setSearchValue(
      key as keyof SearchParams,
      filters
        .split(QUERY_SEPARATOR)
        .filter((el) => el !== filter)
        .join(QUERY_SEPARATOR),
    );
  };

  const isFilters = !!searchValueDebounce || !!companiesValue;
  const isNoData = isFilters && !isFetching && data?.length === 0;
  const isNoCampaigns = !isFetching && data?.length === 1 && data[0].is_demo;

  return (
    <Stack
      sx={{ px: { sm: 8, md: 8, lg: 10, xl: 20 }, pt: 4, pb: 6 }}
      gap="24px"
    >
      <Typography variant="h4" color="custom.textDarkBlue">
        Campaigns
      </Typography>
      <Stack direction="row" gap="8px">
        <InputWithQuery
          maxWidth="350px"
          isDisabled={
            data &&
            data.length === 1 &&
            !Object.values(searchDict).some(Boolean)
          }
        />
        <DropdownWithSearch
          isDisabled={
            data &&
            data.length === 1 &&
            !Object.values(searchDict).some(Boolean)
          }
          placeholder="Company"
          query="company"
          items={companies}
        />
      </Stack>
      {searchDict.company && (
        <Stack direction="row" flexWrap="wrap" gap="8px">
          <SelectedFilterList
            filters={searchDict.company.split(QUERY_SEPARATOR) || []}
            label="Company"
            removeHandler={removeFilterHandler}
          />
        </Stack>
      )}

      {isFetching ? (
        <CustomLoader />
      ) : (
        <>
          {data && data.length > 0 && (
            <>
              {data.length === 1 && data[0].is_demo ? null : (
                <Stack gap="16px">
                  {data.map((product) => {
                    if (product.is_demo) {
                      !demoCampaign && setDemoCampaign(product);
                      return null;
                    }
                    return (
                      <ProductListItem key={product.id} product={product} />
                    );
                  })}

                  <Link to={AppRoutes.CREATE_CAMPAIGN}>
                    <Button
                      variant="contained"
                      fullWidth
                      sx={{
                        backgroundColor: "#FCFCFC",
                        boxShadow: "none",
                      }}
                    >
                      <PlusIcon color="#4C545B" width="32" height="32" />
                    </Button>
                  </Link>
                  {demoCampaign && (
                    <Stack
                      direction="row"
                      width="100%"
                      alignItems="center"
                      justifyContent="center"
                      flexWrap="nowrap"
                      paddingBottom="8px"
                    >
                      <Divider sx={{ flexGrow: 1 }} />
                      <Button
                        onClick={() => setIsDemoCalaps(!isDemoCalaps)}
                        sx={{
                          backgroundColor: "#2194D2",
                          color: "#FFFFFF",
                          width: "216px",
                          borderRadius: "32px",
                          fontSize: "14px",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        Explore Demo Campaign
                        <ChevronLeftIcon
                          rotate={isDemoCalaps ? "90" : "-90"}
                          color="#FFFFFF"
                        />
                      </Button>
                      <Divider sx={{ flexGrow: 1 }} />
                    </Stack>
                  )}

                  {demoCampaign && isDemoCalaps && (
                    <ProductListItem product={demoCampaign} />
                  )}
                </Stack>
              )}
            </>
          )}

          {isNoCampaigns && (
            <Stack gap="16px">
              {!isFilters && <NoCampaignsBlock />}
              <Stack paddingTop="16px"></Stack>
              {data[0] && <ProductListItem product={data[0]} />}
              <Link to={AppRoutes.CREATE_CAMPAIGN}>
                <Button
                  variant="contained"
                  fullWidth
                  sx={{
                    backgroundColor: "#00000005",
                    boxShadow: "none",
                  }}
                >
                  <PlusIcon color="#4C545B" width="32" height="32" />
                </Button>
              </Link>
            </Stack>
          )}

          {isNoData && (
            <Stack flexGrow={1} justifyContent="center" alignItems="center">
              <NoResultFound />
            </Stack>
          )}
        </>
      )}
    </Stack>
  );
};

export default ProductsPage;
